import { QueryClient, useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from 'services/apiRequests';
import type { StringifiedUUID } from 'types';
import { CreateMetricRequest, GetMetricRequest, UpdateMetricRequest, UpdateMetricRequestPayload } from 'types/metrics';
import { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

const metricsUrl = (
  modelId: StringifiedUUID,
  objectId: StringifiedUUID,
  metricId: StringifiedUUID | undefined = undefined,
) => `/api/v1/tq/hierarchy-models/${modelId}/objects/${objectId}/metrics/${metricId ? `${metricId}/` : ''}`;

const createMetric =
  <T>(csrfToken: string, modelId: StringifiedUUID, objectId: StringifiedUUID) =>
  (payload: CreateMetricRequest<T>) => {
    return postRequest<CreateMetricRequest<T>, null>(metricsUrl(modelId, objectId), payload, csrfToken);
  };

const useCreateMetricMutation = <T>(
  csrfToken: string,
  modelId: StringifiedUUID,
  objectId: StringifiedUUID,
  options: UseMutationOptions<null, APIError, CreateMetricRequest<T>> = {},
) => {
  return {
    mutation: useMutation<null, APIError, CreateMetricRequest<T>>({
      ...options,
      mutationFn: createMetric(csrfToken, modelId, objectId),
    }),
  };
};

const updateMetric =
  <T>(csrfToken: string, modelId: StringifiedUUID, objectId: StringifiedUUID) =>
  ({ metricId, payload }: UpdateMetricRequest<T>) => {
    return putRequest<UpdateMetricRequestPayload<T>, null>(metricsUrl(modelId, objectId, metricId), payload, csrfToken);
  };

const useUpdateMetricMutation = <T>(
  csrfToken: string,
  modelId: StringifiedUUID,
  objectId: StringifiedUUID,
  options: UseMutationOptions<null, APIError, UpdateMetricRequest<T>> = {},
) => {
  return {
    mutation: useMutation<null, APIError, UpdateMetricRequest<T>>({
      ...options,
      mutationFn: updateMetric(csrfToken, modelId, objectId),
    }),
  };
};

// PATCH request for partial metric updates (e.g., pivot table configuration)
type PatchMetricVariables = {
  modelId: StringifiedUUID;
  objectId: StringifiedUUID;
  metricId: StringifiedUUID;
  payload: Record<string, unknown>;
};

const patchMetric =
  (csrfToken: string) =>
  ({ modelId, objectId, metricId, payload }: PatchMetricVariables) => {
    return patchRequest<Record<string, unknown>, null>(metricsUrl(modelId, objectId, metricId), payload, csrfToken);
  };

const usePatchMetricMutation = (
  csrfToken: string,
  options: UseMutationOptions<null, APIError, PatchMetricVariables> = {},
) => {
  return {
    mutation: useMutation<null, APIError, PatchMetricVariables>({
      ...options,
      mutationFn: patchMetric(csrfToken),
    }),
  };
};

const deleteMetric =
  (csrfToken: string, modelId: StringifiedUUID, objectId: StringifiedUUID) => (metricId: StringifiedUUID) => {
    return deleteRequest<null, null>(metricsUrl(modelId, objectId, metricId), null, csrfToken);
  };

const useDeleteMetricMutation = (
  csrfToken: string,
  modelId: StringifiedUUID,
  objectId: StringifiedUUID,
  options: UseMutationOptions<null, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<null, APIError, StringifiedUUID>({
      ...options,
      mutationFn: deleteMetric(csrfToken, modelId, objectId),
    }),
  };
};

const getMetricConfigs = (modelId: StringifiedUUID, objectId: StringifiedUUID) => {
  return getRequest<null, GetMetricRequest>(metricsUrl(modelId, objectId));
};

/** Query key shape: ``['metrics', territoryObjectId, hierarchyModelId]`` (see ``useGetMetricConfigsQuery``). */
const invalidateMetricConfigsQueriesForHierarchyModel = (queryClient: QueryClient, hierarchyModelId: StringifiedUUID) =>
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) && key[0] === 'metrics' && key.length >= 3 && String(key[2]) === String(hierarchyModelId)
      );
    },
  });

const useGetMetricConfigsQuery = (
  modelId: StringifiedUUID,
  territoryObjectId: StringifiedUUID,
  options?: QueryHookOptions<GetMetricRequest, APIError>,
) => {
  const QUERY_KEY = ['metrics', territoryObjectId, modelId];
  const queryClient = useQueryClient();
  const query = useQuery<GetMetricRequest, APIError>({
    enabled: !!territoryObjectId,
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getMetricConfigs(modelId, territoryObjectId as string),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

export const MetricsService = {
  useCreateMetricMutation,
  useDeleteMetricMutation,
  useUpdateMetricMutation,
  usePatchMetricMutation,
  useGetMetricConfigsQuery,
  invalidateMetricConfigsQueriesForHierarchyModel,
};
